import styled from "@emotion/styled";
import React, { FC, useState } from "react";
import { theme } from "../../styles/theme";
import { ReactComponent as TrashIcon } from "../../static/icons/trash.svg";
import { News, SITELANGS } from "@balthasarspeyr/common";
import { UpdateNewsInput } from "../../types";

import { gql, useMutation } from "@apollo/client";
import { cloneDeep } from "@apollo/client/utilities";
import { Cell, Row } from "../../styles/styledcomponets";

const langs = Object.keys(SITELANGS);

const UPDATE_NEWS = gql`
  mutation updateNews($id: ID!, $news: NewsInput!) {
    updateNews(id: $id, news: $news) {
      id
      title {
       ${Object.values(SITELANGS).join("\n")}
      }
      body {
       ${Object.values(SITELANGS).join("\n")}
      }
      url {
       ${Object.values(SITELANGS).join("\n")}
      }
      linkText {
       ${Object.values(SITELANGS).join("\n")}
      }
    }
  }
`;

interface NewsEditProps {
  newsProp: UpdateNewsInput & { id: string };
  deleteHandler: () => void;
  refetchHandler: () => void;
}

const NewsEdit: FC<NewsEditProps> = ({
  newsProp,
  deleteHandler,
  refetchHandler
}) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [multilang, setMultilang] = useState<boolean>(false);
  const [news, setNews] = useState<UpdateNewsInput & { id: string }>(
    cloneDeep(newsProp)
  );
  const [backNews, setBackNews] = useState<UpdateNewsInput & { id: string }>(
    cloneDeep(newsProp)
  );
  const [pendingChange, setPendingChange] = useState<boolean>(false);

  const [updateNews] = useMutation<
    { updateNews: News },
    { id: string; news: UpdateNewsInput }
  >(UPDATE_NEWS, {
    onCompleted({ updateNews }) {
      if (updateNews) {
        setEditing(false);
        setPendingChange(false);
        setBackNews(cloneDeep(news));
        refetchHandler();
      }
    },
    onError(error) {
      console.log(error);
    }
  });

  const valueChange = (value, key: string, lang?: string) => {
    const newNews = cloneDeep(news);
    if (lang) newNews[key][lang] = value;
    else newNews[key] = value;
    setNews(newNews);
    setPendingChange(true);
  };

  const onDiscard = () => {
    setEditing(false);
    setPendingChange(false);
    setNews(backNews);
  };

  const onSave = () => {
    const { id, ...newsS } = news;
    updateNews({
      variables: {
        id: news.id!,
        news: newsS
      }
    });
  };

  return (
    <React.Fragment>
      {langs.map(
        (lang) =>
          (multilang || lang === "en") && (
            <Row key={`${lang}${news.id}`}>
              <Cell>
                <Input
                  type="text"
                  name="title"
                  editing={editing}
                  onClick={() => setEditing(true)}
                  onChange={(e) => valueChange(e.target.value, "title", lang)}
                  value={news.title[lang] || ""}
                />
              </Cell>
              <Cell>
                <Input
                  type="text"
                  name="body"
                  editing={editing}
                  onClick={() => setEditing(true)}
                  onChange={(e) => valueChange(e.target.value, "body", lang)}
                  value={news.body?.[lang] || ""}
                />
              </Cell>
              <Cell>
                <Input
                  type="text"
                  editing={editing}
                  name="url"
                  onClick={() => setEditing(true)}
                  onChange={(e) => valueChange(e.target.value, "url", lang)}
                  value={news.url?.[lang] || ""}
                />
              </Cell>
              <Cell>
                <Input
                  type="text"
                  editing={editing}
                  name="linkText"
                  onClick={() => setEditing(true)}
                  onChange={(e) =>
                    valueChange(e.target.value, "linkText", lang)
                  }
                  value={news.linkText?.[lang] || ""}
                />
              </Cell>
              {lang === "en" ? (
                <Cell>
                  <Actions editing={editing}>
                    {multilang ? (
                      <Langs onClick={() => setMultilang(false)}>
                        View only English
                      </Langs>
                    ) : (
                      <Langs onClick={() => setMultilang(true)}>
                        View all langs.
                      </Langs>
                    )}
                    {pendingChange ? (
                      <Save
                        onClick={() => {
                          onSave();
                        }}
                      >
                        Save
                      </Save>
                    ) : null}
                    {pendingChange ? (
                      <Discard
                        onClick={() => {
                          onDiscard();
                        }}
                      >
                        Discard
                      </Discard>
                    ) : null}
                    <TrashIcon
                      onClick={() => {
                        if (
                          confirm(
                            "Are you sure you want to delete this announcement?"
                          )
                        ) {
                          deleteHandler();
                        }
                      }}
                    />
                  </Actions>
                </Cell>
              ) : (
                <Cell>
                  <Actions editing={editing}>{lang}</Actions>
                </Cell>
              )}
            </Row>
          )
      )}
    </React.Fragment>
  );
};

export default NewsEdit;

type InputProps = {
  editing: boolean;
};
const Input = styled.input<InputProps>`
  width: 95%;
  background-color: ${(props) =>
    props.editing ? theme.colors.brandWhite : theme.colors.gray6};
  border: 0px;
  height: 25px;
  font-size: 0.8rem;
  margin-top: auto;
  margin-bottom: auto;
`;

interface IActionsProps {
  editing: boolean;
}
const Actions = styled.div<IActionsProps>`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 25%;
  background-color: ${theme.colors.gray6};
  border: 0px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: stretch;
  img,
  svg {
    fill: ${theme.colors.brandBlack};
    margin: auto;
    margin-left: 5px;
    margin-right: 5px;
    height: 20px;
    min-height: 20px;
    min-width: 20px;
    width: 20px;
    cursor: pointer;
  }
  svg:hover {
    fill: ${theme.colors.brandRed};
  }
`;

const Langs = styled.button`
  background-color: ${theme.colors.brandWhite};
  color: ${theme.colors.brandLang};
  font-weight: bold;
  border-radius: 8px;
  border: 1px solid black;
  outline: none;
  height: 25px;
  flex-basis: auto;
  cursor: pointer;
  margin: auto;
  margin-left: 5px;
  margin-right: 5px;
  padding-left: 10px;
  padding-right: 10px;
`;

const Save = styled.button`
  background-color: ${theme.colors.brandBlack};
  color: ${theme.colors.brandWhite};
  font-weight: bold;
  border-radius: 8px;
  border: none;
  outline: none;
  height: 25px;
  flex-basis: auto;
  cursor: pointer;
  margin: auto;
  margin-left: 5px;
  margin-right: 5px;
`;

const Discard = styled.button`
  background-color: ${theme.colors.brandRed};
  color: ${theme.colors.brandWhite};
  font-weight: bold;
  border-radius: 8px;
  border: none;
  outline: none;
  height: 25px;
  flex-basis: auto;
  cursor: pointer;
  margin: auto;
  margin-left: 5px;
  margin-right: 5px;
`;
